import { FormEvent, useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import Slider, { SliderProps } from '../Slider/Slider';
import styles from './SliderPopover.module.scss';
import { withStyles } from '../../../utils';
import MainButton from '../MainButton/MainButton';
import Card from '../../Layout/Card/Card';
import TextField from '../TextField/TextField';

const cn = withStyles(styles);

export default function SliderPopover({ value, min, max, onApply }: SliderPanelProps) {
  const [vals, setVals] = useState(value);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setVals(value);
  }, [value]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={cn('trigger')}>
        {vals.map((val, ii) => (
          <span key={`${ii}_${val}`} className={cn('triggerVal')}>
            ${formatAsCurrency(val)}
          </span>
        ))}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={cn('popoverContent')} sideOffset={8}>
          <SliderPanel
            value={vals}
            min={min}
            max={max}
            onApply={(_vals) => {
              setVals(_vals);
              onApply(_vals);
              setOpen(false);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

type SliderPanelProps = Omit<SliderProps, 'onValueChange'> & {
  value: number[];
  min: number;
  max: number;
  onApply: (value: number[]) => void;
};

function SliderPanel({ value: valueProp = [], onApply, min, max, ...sliderProps }: SliderPanelProps) {
  const [vals, setVals] = useState<number[]>(valueProp);

  function apply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    onApply(vals);
  }

  function reset() {
    // remove first and last element (they will become min and max)
    const rest = valueProp.slice(1, -1);
    setVals([min, ...rest, max]);
  }

  useEffect(() => {
    setVals(valueProp);
  }, [valueProp]);

  return (
    <Card
      style={{
        borderColor: '#2d2d2d1a',
        borderWidth: 1,
        backgroundColor: '#f2f2f2',
      }}
    >
      <form className={cn('sliderPopover')} onSubmit={apply}>
        <div className={cn('valInputs')}>
          {vals.map((val, ii) => {
            return (
              <TextField
                name=""
                variant="outlineShadow"
                key={ii}
                elementLeft={
                  <span
                    style={{
                      paddingLeft: '14px',
                    }}
                  >
                    $
                  </span>
                }
                value={formatAsCurrency(val)}
                preset="numeric"
                onChange={(e) => {
                  const newValue = Number(e.target.value.replaceAll(',', ''));

                  const newVals = vals.toSpliced(ii, 1, newValue);
                  setVals(newVals);
                }}
              />
            );
          })}
        </div>

        <Slider
          {...sliderProps}
          min={min}
          max={max}
          value={vals}
          onValueChange={(_vals) => {
            setVals(_vals);
          }}
        />

        <div className={cn('minMax')}>
          <span>{formatAsCurrency(min)}</span>
          <span>{formatAsCurrency(max)}</span>
        </div>

        <div className={cn('buttons')}>
          <MainButton size="small" onClick={() => reset()}>
            Reset
          </MainButton>

          <MainButton size="small" type="submit" colorScheme="primary">
            Apply
          </MainButton>
        </div>
      </form>
    </Card>
  );
}

function formatAsCurrency(amount: number) {
  const result = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(amount);

  return result;
}
